import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from '@safe-minds-ai/ui';
import { CheckCheck, CircleDotDashed, ExternalLink, FileText, Inbox, Mail, Paperclip } from 'lucide-react';
import {
  fetchSupportAttachmentDownloadUrl,
  fetchSupportRequests,
  SUPPORT_REQUEST_STATUSES,
  SupportRequest,
  SupportRequestAttachment,
  SupportRequestStatus,
  updateSupportRequestStatus,
} from '../api';
import { useToast } from '../toast';

const formatFileSize = (bytes?: number) => {
  if (!bytes || bytes <= 0) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatMimeType = (value?: string) => {
  if (!value) return 'Unknown type';
  return value.replace(/^application\//, '').replace(/^image\//, '').toUpperCase();
};

const STATUS_FILTERS = ['ALL', ...SUPPORT_REQUEST_STATUSES] as const;
type SupportRequestStatusFilter = (typeof STATUS_FILTERS)[number];

const statusBadgeClassName: Record<SupportRequestStatus, string> = {
  OPEN: 'bg-rose-600 text-white',
  IN_PROGRESS: 'bg-amber-500 text-slate-950',
  RESOLVED: 'bg-emerald-600 text-white',
};

const filterCardMeta: Record<
  SupportRequestStatusFilter,
  { label: string; description: string; icon: typeof Inbox; accent: string }
> = {
  ALL: {
    label: 'All tickets',
    description: 'Every support thread in one view.',
    icon: Inbox,
    accent: 'text-slate-700 dark:text-slate-200',
  },
  OPEN: {
    label: 'Open',
    description: 'Needs first response or triage.',
    icon: Mail,
    accent: 'text-rose-600 dark:text-rose-300',
  },
  IN_PROGRESS: {
    label: 'In progress',
    description: 'Actively being worked by support.',
    icon: CircleDotDashed,
    accent: 'text-amber-600 dark:text-amber-300',
  },
  RESOLVED: {
    label: 'Resolved',
    description: 'Handled and ready to archive.',
    icon: CheckCheck,
    accent: 'text-emerald-600 dark:text-emerald-300',
  },
};

export default function SupportRoute(): JSX.Element {
  const [allSupportRequests, setAllSupportRequests] = useState<SupportRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<SupportRequest | null>(null);
  const [openingAttachmentKey, setOpeningAttachmentKey] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<SupportRequestStatusFilter>('ALL');
  const [updatingRequestId, setUpdatingRequestId] = useState<string | null>(null);
  const toast = useToast();

  const filteredSupportRequests = useMemo(
    () =>
      statusFilter === 'ALL'
        ? allSupportRequests
        : allSupportRequests.filter((request) => request.status === statusFilter),
    [allSupportRequests, statusFilter],
  );

  const statusCounts = useMemo(
    () => ({
      ALL: allSupportRequests.length,
      OPEN: allSupportRequests.filter((request) => request.status === 'OPEN').length,
      IN_PROGRESS: allSupportRequests.filter((request) => request.status === 'IN_PROGRESS').length,
      RESOLVED: allSupportRequests.filter((request) => request.status === 'RESOLVED').length,
    }),
    [allSupportRequests],
  );

  const openAttachment = async (requestId: string, attachment: SupportRequestAttachment) => {
    if (!attachment.s3Key) {
      toast.push({ title: 'Attachment unavailable', description: 'This file does not have a stored S3 key.', variant: 'error' });
      return;
    }

    try {
      setOpeningAttachmentKey(attachment.s3Key);
      const response = await fetchSupportAttachmentDownloadUrl(requestId, attachment.s3Key);
      window.open(response.downloadUrl, '_blank', 'noopener,noreferrer');
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.push({
        title: 'Unable to open attachment',
        description: message || 'Failed to create a download link.',
        variant: 'error',
      });
    } finally {
      setOpeningAttachmentKey(null);
    }
  };

  const handleStatusChange = async (requestId: string, status: SupportRequestStatus) => {
    try {
      setUpdatingRequestId(requestId);
      const updated = await updateSupportRequestStatus(requestId, status);
      setAllSupportRequests((current) => current.map((request) => (request.id === requestId ? updated : request)));
      setSelectedRequest((current) => (current?.id === requestId ? updated : current));
      toast.push({
        title: 'Ticket updated',
        description: `Support request marked ${status.toLowerCase().replace('_', ' ')}.`,
        variant: 'success',
      });
    } catch (err: unknown) {
      const message =
        typeof err === 'object' && err !== null && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      toast.push({
        title: 'Unable to update ticket',
        description: message || 'Failed to update the support request status.',
        variant: 'error',
      });
    } finally {
      setUpdatingRequestId(null);
    }
  };

  useEffect(() => {
    const getSupportRequests = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await fetchSupportRequests();
        setAllSupportRequests(data);
      } catch (err: unknown) {
        const message =
          typeof err === 'object' && err !== null && 'response' in err
            ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined;
        setError('Failed to fetch support requests.');
        toast.push({ title: 'Error', description: message || 'Failed to fetch support requests.', variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    void getSupportRequests();
  }, []);

  if (loading) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Support</p>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Support requests</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">Track and resolve parent support tickets.</p>
        </div>
        <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/80 dark:bg-slate-900/80">
          <CardHeader>
            <CardTitle>Inbox</CardTitle>
            <CardDescription>Newest to oldest</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-center py-8 text-slate-500 dark:text-slate-300">Loading support requests...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Support</p>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Support requests</h1>
          <p className="text-sm text-slate-500 dark:text-slate-300">Track and resolve parent support tickets.</p>
        </div>
        <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/80 dark:bg-slate-900/80">
          <CardHeader>
            <CardTitle>Inbox</CardTitle>
            <CardDescription>Newest to oldest</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <p className="text-center text-rose-500 py-8">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <p className="text-sm font-semibold uppercase tracking-[0.25em] text-primary/70">Support</p>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Support requests</h1>
        <p className="text-sm text-slate-500 dark:text-slate-300">Track and resolve parent support tickets.</p>
      </div>

      <Card className="border border-slate-100 dark:border-slate-700/40 bg-white/80 dark:bg-slate-900/80">
        <CardHeader>
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <CardTitle>Inbox</CardTitle>
              <CardDescription>Newest to oldest with a single resolved end state</CardDescription>
            </div>
            <div className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900/70 dark:text-slate-300">
              Active filter: {filterCardMeta[statusFilter].label}
            </div>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {STATUS_FILTERS.map((filter) => {
              const meta = filterCardMeta[filter];
              const Icon = meta.icon;
              const isActive = statusFilter === filter;
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setStatusFilter(filter)}
                  className={`rounded-2xl border p-4 text-left transition ${
                    isActive
                      ? 'border-primary bg-primary/5 shadow-sm ring-1 ring-primary/20'
                      : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/60 dark:hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">
                        {meta.label}
                      </p>
                      <p className="mt-2 text-3xl font-semibold text-slate-950 dark:text-slate-50">{statusCounts[filter]}</p>
                    </div>
                    <Icon className={`h-5 w-5 ${meta.accent}`} />
                  </div>
                  <p className="mt-3 text-sm text-slate-600 dark:text-slate-300">{meta.description}</p>
                </button>
              );
            })}
          </div>
          <div className="flex flex-wrap gap-2">
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 dark:bg-slate-800 dark:text-slate-300">
              Showing {filteredSupportRequests.length} ticket{filteredSupportRequests.length === 1 ? '' : 's'}
            </span>
            {statusFilter !== 'ALL' ? (
              <Button type="button" variant="ghost" className="rounded-full" onClick={() => setStatusFilter('ALL')}>
                Clear filter
              </Button>
            ) : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {filteredSupportRequests.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-slate-500 dark:text-slate-300">
              <Mail className="mb-4 h-16 w-16" />
              <p className="text-center">
                {statusFilter === 'ALL' ? 'No support requests found.' : `No ${statusFilter.toLowerCase().replace('_', ' ')} support requests found.`}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="dark:border-slate-700/40">
                  <TableHead className="text-slate-500 dark:text-slate-300">ID</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-300">Parent ID</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-300">Subject</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-300">Attachments</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-300">Status</TableHead>
                  <TableHead className="text-slate-500 dark:text-slate-300">Created At</TableHead>
                  <TableHead className="text-right text-slate-500 dark:text-slate-300">Actions</TableHead>
                  <TableHead className="text-right text-slate-500 dark:text-slate-300">Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredSupportRequests.map((request) => (
                  <TableRow key={request.id} className="dark:border-slate-700/40 hover:bg-slate-50 dark:hover:bg-slate-800/80">
                    <TableCell className="font-medium text-slate-900 dark:text-slate-100">{request.id}</TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-100">{request.parentId}</TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-100">{request.subject}</TableCell>
                    <TableCell className="text-slate-700 dark:text-slate-100">
                      <span className="inline-flex items-center gap-2">
                        <Paperclip className="h-4 w-4" />
                        {request.attachments?.length ?? 0}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClassName[request.status]}>{request.status}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 dark:text-slate-300">{new Date(request.createdAt).toLocaleString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        {request.status !== 'RESOLVED' ? (
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-full"
                            disabled={updatingRequestId === request.id}
                            onClick={() => void handleStatusChange(request.id, 'RESOLVED')}
                          >
                            Resolve
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            className="rounded-full"
                            disabled={updatingRequestId === request.id}
                            onClick={() => void handleStatusChange(request.id, 'OPEN')}
                          >
                            Reopen
                          </Button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" className="rounded-full" onClick={() => setSelectedRequest(request)}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedRequest)} onOpenChange={(open) => { if (!open) setSelectedRequest(null); }}>
        <DialogContent className="sm:max-w-[760px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedRequest?.subject ?? 'Support request'}</DialogTitle>
            <DialogDescription>
              Review request details and open any attached files.
            </DialogDescription>
          </DialogHeader>

          {selectedRequest ? (
            <div className="space-y-6">
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Ticket status</p>
                  <div className="mt-2">
                    <Badge className={statusBadgeClassName[selectedRequest.status]}>{selectedRequest.status}</Badge>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selectedRequest.status !== 'IN_PROGRESS' ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      disabled={updatingRequestId === selectedRequest.id}
                      onClick={() => void handleStatusChange(selectedRequest.id, 'IN_PROGRESS')}
                    >
                      Mark in progress
                    </Button>
                  ) : null}
                  {selectedRequest.status !== 'RESOLVED' ? (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      disabled={updatingRequestId === selectedRequest.id}
                      onClick={() => void handleStatusChange(selectedRequest.id, 'RESOLVED')}
                    >
                      Mark resolved
                    </Button>
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-full"
                      disabled={updatingRequestId === selectedRequest.id}
                      onClick={() => void handleStatusChange(selectedRequest.id, 'OPEN')}
                    >
                      Reopen ticket
                    </Button>
                  )}
                </div>
              </div>

              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Parent</p>
                  <p className="mt-2 text-sm text-slate-900 dark:text-slate-100">{selectedRequest.parentId}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Submitted</p>
                  <p className="mt-2 text-sm text-slate-900 dark:text-slate-100">{new Date(selectedRequest.createdAt).toLocaleString()}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Contact</p>
                  <p className="mt-2 text-sm text-slate-900 dark:text-slate-100">{selectedRequest.name || 'Unknown sender'}</p>
                  <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{selectedRequest.email || 'No email provided'}</p>
                </div>
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-700 dark:bg-slate-900/70">
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Category</p>
                  <p className="mt-2 text-sm text-slate-900 dark:text-slate-100">{selectedRequest.category || 'Uncategorized'}</p>
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/70">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Message</p>
                <p className="mt-3 whitespace-pre-wrap text-sm leading-6 text-slate-700 dark:text-slate-100">{selectedRequest.message}</p>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-900/70">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-slate-400">Attachments</p>
                    <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
                      {selectedRequest.attachments?.length ? 'Open uploaded files in a new tab.' : 'No attachments included.'}
                    </p>
                  </div>
                  <Badge className="bg-slate-700 text-white">{selectedRequest.attachments?.length ?? 0}</Badge>
                </div>

                {selectedRequest.attachments?.length ? (
                  <div className="mt-4 space-y-3">
                    {selectedRequest.attachments.map((attachment, index) => {
                      const attachmentKey = attachment.s3Key || `${selectedRequest.id}-${index}`;
                      const isOpening = openingAttachmentKey === attachment.s3Key;
                      return (
                        <div
                          key={attachmentKey}
                          className="flex flex-col gap-3 rounded-2xl border border-slate-200 p-4 dark:border-slate-700 sm:flex-row sm:items-center sm:justify-between"
                        >
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 text-slate-900 dark:text-slate-100">
                              <FileText className="h-4 w-4 shrink-0" />
                              <p className="truncate text-sm font-medium">{attachment.name || `Attachment ${index + 1}`}</p>
                            </div>
                            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                              {formatMimeType(attachment.type)} · {formatFileSize(attachment.size)}
                            </p>
                          </div>
                          <Button
                            className="rounded-full"
                            disabled={!attachment.s3Key || isOpening}
                            onClick={() => void openAttachment(selectedRequest.id, attachment)}
                          >
                            <ExternalLink className="mr-2 h-4 w-4" />
                            {isOpening ? 'Opening...' : 'Open file'}
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
